/**
 * API client wrapper that injects dev-mode authentication headers
 * In development, every request includes:
 * - X-DEV-IS-SUPERADMIN: true
 * - X-DEV-USER-ID: 00000000-0000-0000-0000-000000000001
 */

import { normalizeApiError } from '../utils/adminApiError.js';

const DEV_USER_ID = '00000000-0000-0000-0000-000000000001';
const IS_LOCAL_DEV = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV;

/**
 * Fetches with dev auth headers
 */
// Import portal detection at module level (static import)
import { detectPortal, checkPortalApiGuard } from '../utils/portalDetection.js';

// In production (deployed), always use same-origin relative API; never localhost.
const API_BASE = (() => {
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.PROD) {
    return ''; // same-origin; requests go to /api/v1/...
  }
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) {
    return ''; // Vite dev server proxies /api to backend (see vite.config.js)
  }
  return '';
})();

async function apiFetch(url, options = {}) {
  const resolvedUrl = (API_BASE + url).replace(/^\/+/, '/') || url;
  if (typeof window !== 'undefined' && resolvedUrl.startsWith('http') && resolvedUrl.includes('localhost')) {
    console.error('[ADMIN] API must not use localhost in deployed build. Use same-origin /api/v1.');
  }
  // RUNTIME API BASE LOGGING (first call only)
  if (typeof window !== 'undefined' && !window.__API_BASE_LOGGED) {
    window.__API_BASE_LOGGED = true;
    const origin = window.location.origin;
    const fullUrl = resolvedUrl.startsWith('http') ? resolvedUrl : `${origin}${resolvedUrl}`;
    const portal = detectPortal();
    checkPortalApiGuard(resolvedUrl);
    console.log('[ADMIN] API_BASE=', API_BASE || '(same-origin)');
    console.log('[ADMIN] ORIGIN=', origin);
    console.log('[ADMIN] PORTAL=', portal);
    console.log('[ADMIN] Full URL=', fullUrl);
    console.log('[ADMIN] Relative URL=', resolvedUrl);
  }

  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'X-DEV-IS-SUPERADMIN': 'true',
    'X-DEV-USER-ID': DEV_USER_ID,
    ...(IS_LOCAL_DEV ? { 'X-Test-Mode': 'true' } : {}),
    ...options.headers,
  };

  try {
    const response = await fetch(resolvedUrl, {
      ...options,
      headers,
      credentials: 'include', // Include session cookies for authentication
      signal: options.signal, // Support AbortController signal
    });

    if (!response.ok) {
      // Check if response is HTML (nginx 401 page)
      const contentType = response.headers.get('content-type') || '';
      const isHTML = contentType.includes('text/html');
      
      let errorData;
      if (response.status === 401 || response.status === 403) {
        // Handle 401/403 as "not authenticated" - return structured error that can be handled gracefully
        if (isHTML) {
          // HTML response (likely nginx 401 page) - treat as unauthenticated
          await response.text(); // Consume body
          errorData = {
            message: 'Admin access requires authentication',
            errorCode: 'UNAUTHENTICATED',
            status: response.status,
            isHTML: true,
          };
        } else {
          // JSON error response
          try {
            errorData = await response.json();
            errorData.status = response.status;
            errorData.errorCode = errorData.errorCode || 'UNAUTHENTICATED';
          } catch (e) {
            errorData = {
              message: 'Admin access requires authentication',
              errorCode: 'UNAUTHENTICATED',
              status: response.status,
            };
          }
        }
      } else {
        // Other errors - try to parse as JSON
        if (isHTML) {
          await response.text(); // Consume body
          errorData = {
            message: response.statusText,
            errorCode: 'UNKNOWN_ERROR',
            status: response.status,
            isHTML: true,
          };
        } else {
          try {
            errorData = await response.json();
            errorData.status = response.status;
            errorData.errorCode = errorData.errorCode || 'UNKNOWN_ERROR';
          } catch (e) {
            errorData = {
              message: response.statusText,
              errorCode: 'UNKNOWN_ERROR',
              status: response.status,
            };
          }
        }
      }
      
      // Extract request ID from headers if available
      const requestId = response.headers.get('x-request-id') || 
                        response.headers.get('x-clc-request-id') || 
                        errorData.requestId || null;
      
      // Create normalized error
      const apiError = new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      apiError.status = response.status;
      apiError.code = errorData.errorCode || errorData.code || (response.status === 401 ? 'UNAUTHENTICATED' : 'UNKNOWN_ERROR');
      errorData.detail = errorData.detail || null;
      apiError.requestId = requestId;
      apiError.isHTML = isHTML;
      apiError.body = errorData;
      
      // For 429, extract retry-after
      if (response.status === 429) {
        const retryAfter = response.headers.get('retry-after');
        if (retryAfter) {
          apiError.detail = { ...apiError.detail, retryAfter: parseInt(retryAfter, 10) };
        }
      }
      
      throw apiError;
    }

    // Parse response
    const data = await response.json();
    
    // If response has 'outcome' field, use it; otherwise treat as success
    if (data && data.outcome === 'error') {
      const apiError = new Error(data.message || 'Request failed');
      apiError.status = response.status;
      apiError.code = data.errorCode || data.code || 'UNKNOWN_ERROR';
      apiError.detail = data.detail || null;
      apiError.requestId = data.requestId || 
                           response.headers.get('x-request-id') || 
                           response.headers.get('x-clc-request-id') || null;
      throw apiError;
    }
    
    // Handle both wrapped and unwrapped responses:
    // - Wrapped: { data: { ... } } -> return data.data
    // - Unwrapped: { integrations: [...] } -> return as-is (admin endpoints)
    if (data && typeof data === 'object' && 'data' in data) {
      // Wrapped response - return unwrapped payload
      return data.data;
    }
    // Unwrapped response (admin endpoints) - return as-is
    return data;
  } catch (err) {
    // If already a normalized error, re-throw
    if (err.status !== undefined) {
      throw err;
    }
    
    // Network errors or other issues - normalize them
    const normalized = normalizeApiError({
      message: err.message || 'Network error',
      status: null,
      code: 'NETWORK_ERROR',
    });
    
    const apiError = new Error(normalized.message);
    Object.assign(apiError, normalized);
    throw apiError;
  }
}

/**
 * GET request
 * @param {string} endpoint - API endpoint
 * @param {Object} [options] - Optional fetch options (e.g., { signal: AbortSignal })
 */
export async function apiGet(endpoint, options = {}) {
  return apiFetch(`/api/v1${endpoint}`, options);
}

/**
 * GET a binary resource (file/thumbnail) as a Blob, with the same auth as apiFetch
 * (session cookie + dev headers). Returns { blob, contentType } or null for 204.
 * Throws a normalized error on non-OK responses.
 */
export async function apiGetBlob(endpoint, options = {}) {
  const resolvedUrl = `/api/v1${endpoint}`;
  const headers = {
    'X-DEV-IS-SUPERADMIN': 'true',
    'X-DEV-USER-ID': DEV_USER_ID,
    ...(IS_LOCAL_DEV ? { 'X-Test-Mode': 'true' } : {}),
    ...(options.headers || {}),
  };
  const response = await fetch(resolvedUrl, {
    method: 'GET',
    headers,
    credentials: 'include',
    signal: options.signal,
  });
  if (response.status === 204) return null;
  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}`);
    err.status = response.status;
    err.code = response.status === 401 || response.status === 403 ? 'UNAUTHENTICATED' : 'UNKNOWN_ERROR';
    throw err;
  }
  const blob = await response.blob();
  return { blob, contentType: response.headers.get('content-type') || blob.type || '' };
}

/**
 * POST request
 */
export async function apiPost(endpoint, data) {
  return apiFetch(`/api/v1${endpoint}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** POST multipart (FormData). Do not JSON.stringify. */
export async function apiPostForm(endpoint, formData) {
  return apiFetch(`/api/v1${endpoint}`, {
    method: 'POST',
    body: formData,
  });
}

/**
 * PUT request
 */
export async function apiPut(endpoint, data) {
  return apiFetch(`/api/v1${endpoint}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * PATCH request
 */
export async function apiPatch(endpoint, data) {
  return apiFetch(`/api/v1${endpoint}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * DELETE request
 */
export async function apiDelete(endpoint) {
  return apiFetch(`/api/v1${endpoint}`, {
    method: 'DELETE',
  });
}

export default { apiGet, apiPost, apiPostForm, apiPut, apiPatch, apiDelete };
